import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    GraduationCap, UserPlus, X, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, ShieldCheck, Award, BookOpen, Trash2, Search, Edit3, Eye, EyeOff 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const Enseignants = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    const [activeTab, setActiveTab] = useState('tous');
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null); // ID de l'enseignant en cours de modification
    const [showPassword, setShowPassword] = useState(false); // État pour afficher/masquer le mot de passe
    
    const [enseignants, setEnseignants] = useState([]);
    const [matieresCatalogue, setMatieresCatalogue] = useState([]);
    const [enseignantsOuverts, setEnseignantsOuverts] = useState({});

    const [ensForm, setEnsForm] = useState({
        username: '',
        email: '',
        password: '',
        telephone: '',
        adresse: '',
        matricule: '',
        fonction: 'Professeur'
    });

    // États pour le système 3 colonnes (Catalogue / Ajout / Panier)
    const [lignesSpecialites, setLignesSpecialites] = useState([]);
    const [selectedMatiere, setSelectedMatiere] = useState(null);
    const [searchTermMatiere, setSearchTermMatiere] = useState('');

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [resEns, resMat] = await Promise.all([
                API.get('/scolarite/enseignants/liste'),
                API.get('/scolarite/matieres/liste').catch(() => ({ data: { success: false, data: [] } }))
            ]);
            if (resEns.data.success) setEnseignants(resEns.data.data);
            if (resMat.data.success) setMatieresCatalogue(resMat.data.data);
        } catch (err) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (['users', 'enseignant_specialites', 'matieres'].includes(event.table)) {
                    fetchData();
                }
            });
        }
        return () => {
            if (socket) socket.off('DATA_EVENT');
        };
    }, [fetchData, user?.company_id]);

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setShowPassword(false);
        setEnsForm({
            username: '',
            email: '',
            password: '',
            telephone: '',
            adresse: '',
            matricule: '',
            fonction: 'Professeur'
        });
        setLignesSpecialites([]);
        setSelectedMatiere(null);
        setSearchTermMatiere('');
    };

    // Préparer le formulaire pour la modification
    const handleEditEnseignant = (ens, e) => {
        e.stopPropagation(); // Évite de déclencher l'accordéon
        setEditingId(ens.id);
        setShowPassword(false);
        setEnsForm({
            username: ens.username || '',
            email: ens.email || '',
            password: '', // On laisse vide pour ne pas écraser inutilement si non modifié
            telephone: ens.telephone || '',
            adresse: ens.adresse || '',
            matricule: ens.matricule || '',
            fonction: ens.fonction || 'Professeur'
        });

        // Charger les spécialités existantes dans le panier
        const existingSpecs = (ens.specialites || []).map(s => ({
            id: `SPEC-${s.matiere_id || s.id}-${Date.now()}`,
            matiere_id: s.matiere_id || s.id,
            nom: s.libelle,
            code_matiere: s.code_matiere
        }));
        setLignesSpecialites(existingSpecs);
        setShowForm(true);
    };

    const toggleAccordion = (id) => {
        setEnseignantsOuverts(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleSelectMatiere = (mat) => {
        setSelectedMatiere(mat);
    };

    const confirmAddSpecialite = () => {
        if (!selectedMatiere) return showToast("Veuillez sélectionner une matière dans la liste", "error");
        if (lignesSpecialites.some(l => (l.matiere_id || l.prestation_id) === selectedMatiere.id)) {
            return showToast("Cette spécialité est déjà dans le panier", "error");
        }

        const newSpecialite = {
            id: `SPEC-${Date.now()}`,
            matiere_id: selectedMatiere.id,
            nom: selectedMatiere.libelle,
            code_matiere: selectedMatiere.code_matiere
        };

        setLignesSpecialites([...lignesSpecialites, newSpecialite]);
        setSelectedMatiere(null);
    };

    const handleSaveEnseignant = async (e) => {
        e.preventDefault();
        if (lignesSpecialites.length === 0) {
            showToast("Veuillez ajouter au moins une spécialité dans le panier.", "error");
            return;
        }

        setActionLoading(true);
        try {
            const payload = {
                ...ensForm,
                specialites_ids: lignesSpecialites.map(l => l.matiere_id || l.prestation_id)
            };

            if (editingId) {
                await API.put(`/scolarite/enseignants/${editingId}`, payload);
                showToast("Enseignant mis à jour avec succès.");
            } else {
                await API.post('/scolarite/enseignants/creer', payload);
                showToast("Enseignant créé avec succès.");
            }

            resetForm();
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de l'enregistrement de l'enseignant", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const enseignantsFiltres = useMemo(() => {
        if (activeTab === 'titulaires') {
            return enseignants.filter(e => e.classes_titulaires && e.classes_titulaires.length > 0);
        }
        return enseignants;
    }, [enseignants, activeTab]);

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><GraduationCap size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>GESTION DES ENSEIGNANTS</h1>
                            <div style={subtitleStyle}><ShieldCheck size={14} /> Corps professoral et Spécialités</div>
                        </div>
                    </div>
                    <button 
                        onClick={() => { if (showForm) resetForm(); else setShowForm(true); }} 
                        style={showForm ? btnCancel : btnAdd}
                    >
                        {showForm ? <><X size={18} /> ANNULER</> : <><UserPlus size={18} /> NOUVEL ENSEIGNANT</>}
                    </button>
                </header>

                <div style={tabsBarContainer}>
                    <button onClick={() => setActiveTab('tous')} style={activeTab === 'tous' ? activeTabBtn : tabBtn}>
                        <GraduationCap size={16} /> Tous ({enseignants.length})
                    </button>
                    <button onClick={() => setActiveTab('titulaires')} style={activeTab === 'titulaires' ? activeTabBtn : tabBtn}>
                        <Award size={16} /> Professeurs Titulaires
                    </button>
                </div>

                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#2563eb', textTransform: 'uppercase'}}>
                                {editingId ? "📝 Modification de la fiche enseignant" : "📝 Enregistrement d'un nouvel enseignant & sélection des spécialités"}
                            </span>
                            <button onClick={resetForm} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSaveEnseignant} style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px'}}>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>NOM & PRÉNOMS *</label>
                                    <input style={inputStyle} required placeholder="Ex: Dr. Koffi Mensah" value={ensForm.username} onChange={e => setEnsForm({...ensForm, username: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>EMAIL PROFESSIONNEL *</label>
                                    <input type="email" style={inputStyle} required placeholder="prof@ecole.com" value={ensForm.email} onChange={e => setEnsForm({...ensForm, email: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>MOT DE PASSE {editingId ? "(Laisser vide pour ne pas modifier)" : "*"}</label>
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        <input 
                                            type={showPassword ? "text" : "password"} 
                                            style={{ ...inputStyle, paddingRight: '35px' }} 
                                            required={!editingId} 
                                            placeholder="Définir un mot de passe" 
                                            value={ensForm.password} 
                                            onChange={e => setEnsForm({...ensForm, password: e.target.value})} 
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setShowPassword(!showPassword)} 
                                            style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                                            title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>MATRICULE</label>
                                    <input style={inputStyle} placeholder="Ex: PROF-001" value={ensForm.matricule} onChange={e => setEnsForm({...ensForm, matricule: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>TÉLÉPHONE</label>
                                    {/* 🟢 Restriction numérique stricte (chiffres et + autorisés) */}
                                    <input 
                                        type="tel" 
                                        inputMode="numeric" 
                                        style={inputStyle} 
                                        placeholder="Ex: +22890000000" 
                                        value={ensForm.telephone} 
                                        onChange={e => {
                                            const val = e.target.value.replace(/[^0-9+]/g, '');
                                            setEnsForm({...ensForm, telephone: val});
                                        }} 
                                    />
                                </div>
                            </div>

                            {/* SYSTÈME EN 3 COLONNES POUR LES SPÉCIALITÉS */}
                            <div style={{display: 'grid', gridTemplateColumns: '1.2fr 1.1fr 1.2fr', gap: '15px', marginTop: '10px'}}>
                                
                                {/* COLONNE 1 : Catalogue des matières */}
                                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px' }}>
                                        1. CATALOGUE DES MATIÈRES (CLIQUER)
                                    </div>
                                    <input 
                                        type="text" 
                                        placeholder="Filtrer une matière..." 
                                        style={{ width: '100%', padding: '6px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #cbd5e1', marginBottom: '8px', boxSizing: 'border-box' }}
                                        value={searchTermMatiere}
                                        onChange={(e) => setSearchTermMatiere(e.target.value)}
                                    />
                                    <div style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {matieresCatalogue
                                                    .filter(mat => !lignesSpecialites.some(l => (l.matiere_id || l.prestation_id) === mat.id))
                                                    .filter(mat => mat.libelle.toLowerCase().includes(searchTermMatiere.toLowerCase()) || (mat.code_matiere && mat.code_matiere.toLowerCase().includes(searchTermMatiere.toLowerCase())))
                                                    .map(mat => {
                                                        const isSelected = selectedMatiere?.id === mat.id;
                                                        return (
                                                            <tr 
                                                                key={mat.id} 
                                                                onClick={() => handleSelectMatiere(mat)}
                                                                style={{ cursor: 'pointer', background: isSelected ? '#eff6ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                                                            >
                                                                <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: isSelected ? '800' : '600', color: isSelected ? '#2563eb' : '#334155' }}>
                                                                    {mat.libelle}
                                                                </td>
                                                                <td style={{ padding: '8px 10px', fontSize: '11px', fontWeight: '800', color: '#64748b', textAlign: 'right' }}>
                                                                    {mat.code_matiere}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                {matieresCatalogue.length === 0 && (
                                                    <tr>
                                                        <td colSpan="2" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '11px' }}>
                                                            Aucune matière dans le catalogue
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* COLONNE 2 : Paramétrage & Ajout */}
                                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px' }}>
                                            2. VALIDATION DE LA SPÉCIALITÉ
                                        </div>
                                        <div style={{ fontSize: '12px', fontWeight: '800', color: '#2563eb', marginBottom: '12px', background: '#e0f2fe', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                                            {selectedMatiere ? `${selectedMatiere.libelle} (${selectedMatiere.code_matiere})` : '👉 Sélectionnez une matière à gauche'}
                                        </div>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={confirmAddSpecialite}
                                        disabled={!selectedMatiere}
                                        style={{ width: '100%', padding: '10px', background: selectedMatiere ? '#2563eb' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', cursor: selectedMatiere ? 'pointer' : 'not-allowed', fontSize: '11px' }}
                                    >
                                        + Ajouter au Panier des Spécialités
                                    </button>
                                </div>

                                {/* COLONNE 3 : Panier */}
                                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>3. PANIER ({lignesSpecialites.length})</span>
                                        <span style={{ color: '#059669' }}>{lignesSpecialites.length} spécialité(s)</span>
                                    </div>
                                    <div style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {lignesSpecialites.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="2" style={{ textAlign: 'center', padding: '20px', color: '#ef4444', fontSize: '11px', fontWeight: '700' }}>
                                                            Panier vide (Au moins 1 requis)
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    lignesSpecialites.map(l => (
                                                        <tr key={l.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                            <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>
                                                                {l.nom} <span style={{ color: '#64748b', fontSize: '10px' }}>({l.code_matiere})</span>
                                                            </td>
                                                            <td style={{ padding: '8px 10px', textAlign: 'right', width: '30px' }}>
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => {
                                                                        setLignesSpecialites(lignesSpecialites.filter(x => x.id !== l.id));
                                                                        if (selectedMatiere?.id === l.matiere_id) setSelectedMatiere(null);
                                                                    }} 
                                                                    style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer' }}
                                                                >
                                                                    <Trash2 size={12}/>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                            </div>

                            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px'}}>
                                <button type="button" onClick={resetForm} style={btnCancel}>
                                    ANNULER
                                </button>
                                <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                    {editingId ? "METTRE À JOUR L'ENSEIGNANT" : "ENREGISTRER L'ENSEIGNANT"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><GraduationCap className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {enseignantsFiltres.map(ens => {
                                const ouvert = Boolean(enseignantsOuverts[ens.id]);
                                const specialites = ens.specialites || [];
                                const classesTitulaire = ens.classes_titulaires || [];
                                return (
                                    <div key={ens.id} style={classeCardStyle}>
                                        <div onClick={() => toggleAccordion(ens.id)} style={classeHeaderStyle}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                                                <span style={chevronBox}>{ouvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                                                <span style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>👨‍🏫 {ens.username}</span>
                                                <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
                                                    {specialites.map(s => <span key={s.id || s.matiere_id} style={{background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800'}}>{s.libelle}</span>)}
                                                </div>
                                                {classesTitulaire.length > 0 && <span style={{background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800'}}>Titulaire</span>}
                                            </div>
                                            
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                <span style={{fontSize: '11px', color: '#64748b', fontWeight: '700'}}>Matricule : {ens.matricule || 'N/A'}</span>
                                                <button 
                                                    onClick={(e) => handleEditEnseignant(ens, e)}
                                                    style={{ background: '#e0f2fe', color: '#0284c7', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                                >
                                                    <Edit3 size={12} /> Modifier
                                                </button>
                                            </div>
                                        </div>
                                        {ouvert && (
                                            <div style={classeBodyStyle}>
                                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '12px'}}>
                                                    <div style={{background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                        <strong style={{color: '#2563eb'}}>Spécialités enregistrées :</strong>
                                                        <ul style={{margin: '5px 0 0 15px', padding: 0, color: '#475569'}}>
                                                            {specialites.length > 0 ? specialites.map((s, idx) => <li key={idx}>{s.libelle} ({s.code_matiere})</li>) : <li style={{fontStyle: 'italic', color: '#94a3b8'}}>Aucune spécialité définie.</li>}
                                                        </ul>
                                                    </div>
                                                    <div style={{background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                        <strong style={{color: '#16a34a'}}>Classes dont il est titulaire :</strong>
                                                        <ul style={{margin: '5px 0 0 15px', padding: 0, color: '#475569'}}>
                                                            {classesTitulaire.length > 0 ? classesTitulaire.map((c, idx) => <li key={idx}>Classe {c.nom_classe}</li>) : <li style={{fontStyle: 'italic', color: '#94a3b8'}}>Aucune classe en tant que titulaire.</li>}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {enseignantsFiltres.length === 0 && <div style={emptyStyle}>Aucun enseignant trouvé.</div>}
                        </div>
                    )}
                </div>

                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// Styles communs
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const tabsBarContainer = { display: 'flex', alignItems: 'center', background: 'white', padding: '10px 40px', borderBottom: '1px solid #e2e8f0', gap: '10px' };
const tabBtn = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f5f9', border: 'none', padding: '10px 20px', borderRadius: '10px', fontSize: '12px', fontWeight: '800', color: '#64748b', cursor: 'pointer' };
const activeTabBtn = { ...tabBtn, background: '#0f172a', color: 'white' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const classeCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' };
const classeHeaderStyle = { background: '#f8fafc', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const chevronBox = { background: '#e2e8f0', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' };
const classeBodyStyle = { padding: '15px 20px' };
const emptyStyle = { textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '600' };
const formWrapper = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '200px' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '12px', width: '100%', background: 'white' };
const btnSubmit = { background: '#059669', color: 'white', border: 'none', padding: '9px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', height: '38px', fontSize: '11px' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default Enseignants;